import { State, Action, StateContext, Selector, Store, Select } from '@ngxs/store';
import { patch } from '@ngxs/store/operators';
import { Injectable } from '@angular/core';
import * as OrderActions from './order.action';
import { OrderService } from './order.service';
import { BannerService } from '../../shared/services/banner.service';
import * as OrderModels from '../../models/order/order';
import * as OrderStatusModels from '../../models/order/orderStatus';
import * as OrderTypeModels from '../../models/order/orderType';
import { OrderSearchModel } from '../../models/order/orderSearch';
import { HelperService } from '../../../../src/app/services/helper/helper.service';
import { saveAs } from 'file-saver';
import * as Models from '../../models/commonModels';
import * as OrderNoteModels from '../../models/order/order-note';
import { StorageService } from '../../shared/services/storage.service';


export interface OrderStateModel {
  OrderStatus: OrderStatusModels.OrderStatusListModel;
  OrderTypes: OrderTypeModels.OrderTypeListModel;
  OrderList: OrderModels.OrderListModel;
  Order: OrderModels.OrderModel;
  Search: OrderSearchModel;
  Save: Models.SaveState;
  ProcessingReportFiltered: number;
  ProcessingReportNFM: number;
  OrderNotes: Models.ResponseList<OrderNoteModels.OrderNoteModel>
}


class DictionaryKeys {
  public static readonly OrderTypes = "OrderTypes";
}

@State<OrderStateModel>({
  name: 'OrderStateModel',
  defaults: {
    OrderStatus: null,
    OrderTypes: null,
    OrderList: null,
    Search: null,
    Save: null,
    Order: null,
    ProcessingReportFiltered: null,
    ProcessingReportNFM: null,
    OrderNotes: null
  }
})

@Injectable()

export class OrderState {

  constructor(
    private helperService: HelperService,
    private orderService: OrderService,
    private bannerService: BannerService,
    private storageService: StorageService
  ) { }


  @Selector()
  static OrderStatus(state: OrderStateModel) {
    return state.OrderStatus;
  }

  @Selector()
  static OrderTypes(state: OrderStateModel) {
    return state.OrderTypes;
  }

  @Selector()
  static OrderList(state: OrderStateModel) {
    return state.OrderList;
  }

  @Selector()
  static Order(state: OrderStateModel) {
    return state.Order;
  }

  @Selector()
  static SaveOrder(state: OrderStateModel) {
    return state.Save;
  }

  @Selector()
  static OrderSearch(state: OrderStateModel) {
    return state.Search;
  }

  @Selector()
  static ProcessingReportFiltered(state: OrderStateModel) {
    return state.ProcessingReportFiltered;
  }

  @Selector()
  static ProcessingReportNFM(state: OrderStateModel) {
    return state.ProcessingReportNFM;
  }

  @Selector()
  static OrderNotes(state: OrderStateModel) {
    return state.OrderNotes;
  }

  @Action(OrderActions.GetOrderStatusDictionary)
  GetOrderStatusDictionary(
    context: StateContext<OrderStateModel>,
    action: OrderActions.GetOrderStatusDictionary
  ) {

    const errorMessage = 'Błąd pobierania statusów zleceń.';

    const state = context.getState();

    this.orderService.getOrderStatusDictionary(action.OrderTypeId).subscribe((ret) => {
      if (ret.Success) {
        context.setState(
          patch({
            OrderStatus: ret
          })
        );
      } else {
        this.bannerService.error(ret.Message, errorMessage);
      }
    },
      (error) => {
        this.helperService.SaveException('OrderActions.GetOrderStatusDictionary', error);

        this.bannerService.error(errorMessage);
      });
  }

  @Action(OrderActions.GetOrderTypeDictionary)
  GetOrderTypeDictionary(
    context: StateContext<OrderStateModel>
  ) {

    const errorMessage = 'Błąd pobierania typów zleceń.';

    let storageKey = DictionaryKeys.OrderTypes;

    context.setState(
      patch({
        OrderTypes: this.storageService.getItemJSON(storageKey)
      })
    );

    const state = context.getState();

    if (!state.OrderTypes) {
      this.orderService.getOrderTypeDictionary().subscribe((ret) => {
        if (ret.Success) {

          context.setState(
            patch({
              OrderTypes: ret
            })
          );

          this.storageService.setItemJSON(storageKey, ret);

        } else {
          this.bannerService.error(ret.Message, errorMessage);
        }
      },
        (error) => {
          this.helperService.SaveException('OrderActions.GetOrderTypeDictionary', error);

          this.bannerService.error(errorMessage);
        });
    }
  }


  @Action(OrderActions.GetList)
  GetList(
    context: StateContext<OrderStateModel>,
    action: OrderActions.GetList
  ) {

    let errorMessage = 'Błąd pobierania listy zleceń';

    context.setState(
      patch({
        OrderList: null
      })
    );

    this.orderService.get(action.Search).subscribe((ret) => {
      if (ret.Success) {
        context.setState(
          patch({
            OrderList: ret
          })
        );

        return ret;

      } else {
        this.bannerService.error(ret.Message, errorMessage);
      }
    },
      (error) => {
        this.helperService.SaveException('OrderActions.GetList', error);

        this.bannerService.error(errorMessage);
      });
  }


  @Action(OrderActions.SetSearch)
  SetSearch(
    context: StateContext<OrderStateModel>,
    action: OrderActions.SetSearch
  ) {

    action.Search.CurrentPage = 1;

    context.setState(
      patch({
        Search: action.Search
      }));

    context.dispatch(new OrderActions.GetList(action.Search));
  }

  @Action(OrderActions.SetListPage)
  setListPage(
    context: StateContext<OrderStateModel>,
    action: OrderActions.SetListPage
  ) {

    context.setState(
      patch({
        Search: action.Search
      }));

    context.dispatch(new OrderActions.GetList(action.Search));
  }

  @Action(OrderActions.ResetSearch)
  ResetSearch(
    context: StateContext<OrderStateModel>
  ) {

    const state = context.getState();

    let search = new OrderSearchModel();

    search.ItemsPerPage = state.Search.ItemsPerPage;

    context.setState(
      patch({
        Search: search
      }));

    context.dispatch(new OrderActions.GetList(search));
  }

  @Action(OrderActions.Insert)
  Insert(
    context: StateContext<OrderStateModel>,
    action: OrderActions.Insert
  ) {
    let errorMessage = 'Błąd utworzenia nowego zlecenia.';

    this.orderService.insert(action.Order).subscribe(
      ret => {
        this.bannerService.success('Utworzono nowe zlecenie.');

        action.Order.OrderId = Number(ret);

        context.setState(
          patch({
            Save: new Models.SaveState(action.Order.OrderId, true, action.Version)
          })
        );
      },

      (error) => {

        context.setState(
          patch({
            Save: new Models.SaveState(action.Order.OrderId, false, action.Version)
          })
        );

        this.helperService.SaveException('GarageActions.Set', error);

        this.bannerService.error(errorMessage);
      },
      () => { });
  }

  @Action(OrderActions.Set)
  Set(
    context: StateContext<OrderStateModel>,
    action: OrderActions.Set
  ) {
    const errorMessage = 'Błąd zapisu szczegółów zlecenia.';

    this.orderService.set(action.Order).subscribe(
      ret => {
        this.bannerService.success('Zmiany szczegółów zlecenia zapisane.');

        context.setState(
          patch({
            Save: new Models.SaveState(action.Order.OrderId, true)
          })
        );
      },

      (error) => {

        context.setState(
          patch({
            Save: new Models.SaveState(action.Order.OrderId, false)
          })
        );

        this.helperService.SaveException('GarageActions.Set', error);

        this.bannerService.error(errorMessage);
      },
      () => { });
  }

  @Action(OrderActions.Get)
  Get(
    context: StateContext<OrderStateModel>,
    action: OrderActions.Get
  ) {
    context.setState(
      patch({
        Order: null
      })
    );

    let errorMessage = 'Błąd pobierania szczegółów zlecenia.';

    this.orderService.getOrder(action.OrderId).subscribe((ret) => {
      if (ret.Success) {
        context.setState(
          patch({
            Order: ret.Item
          })
        );
      } else {
        this.bannerService.error(ret.Message, errorMessage);
      }
    },
      (error) => {
        this.helperService.SaveException('GarageActions.Get', error);

        this.bannerService.error(errorMessage);
      });
  }

  @Action(OrderActions.GetReportFiltered)
  GetReportFiltered(
    context: StateContext<OrderStateModel>,
    action: OrderActions.GetReportFiltered
  ) {

    this.bannerService.quickInfo('Rozpoczęto generowanie raportu, proszę czekać.');


    this.orderService.getReportFiltered(action.Search).subscribe(ret => {
      const file = new File([ret], `Zlecenia-${this.helperService.TimeStamp()}.xlsx`, { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      saveAs(file);
    },
      (error) => {

        this.helperService.SaveException('OrderActions.GetReportFiltered', error);

        this.bannerService.error('Błąd pobierania raportu zleceń.');
      },
      () => {

        context.setState(
          patch({
            ProcessingReportFiltered: Math.random()
          })
        );
      }
    );
  }

  @Action(OrderActions.GetReportNFM)
  GetReportNFM(
    context: StateContext<OrderStateModel>,
    action: OrderActions.GetReportFiltered
  ) {

    this.bannerService.quickInfo('Rozpoczęto generowanie raportu NFM, proszę czekać.');


    this.orderService.getReportNFM(action.Search).subscribe(ret => {
      const file = new File([ret], `Zlecenia-${this.helperService.TimeStamp()}.xlsx`, { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      saveAs(file);
    },
      (error) => {

        this.helperService.SaveException('OrderActions.GetReportFiltered', error);

        this.bannerService.error('Błąd pobierania raportu NFM.');
      },
      () => {

        context.setState(
          patch({
            ProcessingReportNFM: Math.random()
          })
        );
      }
    );
  }

  @Action(OrderActions.DisposeSearch)
  DisposeSearch(
    context: StateContext<OrderStateModel>
  ) {

    context.setState(
      patch({
        Search: null
      }));
  }

  @Action(OrderActions.SetOrderNote)
  SetOrderNote(
    context: StateContext<OrderStateModel>,
    action: OrderActions.SetOrderNote
  ) {
    const errorMessage = 'Błąd wysłania wiadomości do zlecenia.';

    this.orderService.setOrderNote(action.OrderNote).subscribe(
      ret => {

        if (ret.Success) {
          this.bannerService.success('Wysłano nową wiadomość do zlecenia.');

          context.setState(
            patch({
              OrderNotes: ret
            })
          );
        } else {
          this.bannerService.error(ret.Message, errorMessage);
        }
      },

      (error) => {

        this.helperService.SaveException('OrderActions.SetOrderNote', error);

        this.bannerService.error(errorMessage);
      },
      () => { });
  }

  @Action(OrderActions.GetOrderNotes)
  GetOrderNotes(
    context: StateContext<OrderStateModel>,
    action: OrderActions.GetOrderNotes
  ) {
    const errorMessage = 'Błąd odczytu wiadomości zlecenia.';

    this.orderService.getOrderNotes(action.OrderId).subscribe(
      ret => {

        if (ret.Success) {

          context.setState(
            patch({
              OrderNotes: ret
            })
          );
        } else {
          this.bannerService.error(ret.Message, errorMessage);
        }
      },

      (error) => {

        this.helperService.SaveException('OrderActions.GetOrderNotes', error);

        this.bannerService.error(errorMessage);
      },
      () => { });
  }
}

